import {
  AssetTypes,
  FOLDER_TRASH_ID,
  HIDE_MOVE_ZAP_WARNING,
} from "@zapier/utils";
import { MoveModalInner } from "./core/MoveModalInner";
import { MoveModalProps, OnMoveOptions } from "./core/types";
import { useMoveZapActions } from "./useMoveZapActions";

export function MoveZapsModal({
  assetIds,
  assetsAreShared = false,
  assetParentId,
  assetTitle,
  currentFolderId,
  isOpen,
  isAssetOwnedByUser = false,
  onSettled,
  onShowConfirmMoveToSharedFolder,
  onShowConfirmMoveToTrash,
  setIsOpen,
}: MoveModalProps) {
  const { moveZapsToFolderWithToasts, undoMoveZapsToFolderWithToasts } =
    useMoveZapActions({
      assetIds,
      assetTitle,
      assetsAreShared,
      currentFolderId,
      isAssetOwnedByUser,
      onSettled,
    });

  const handleMoveZapsToFolder = ({
    selectedFolderId,
    selectedFolderName,
    selectedFolderIsShared,
  }: OnMoveOptions) => {
    if (!selectedFolderId) {
      return;
    }

    if (selectedFolderId === FOLDER_TRASH_ID) {
      onShowConfirmMoveToTrash?.();
      return;
    }

    // Show move zap to shared folder confirmation if target folder is shared
    // and any of the selected zaps are not in a shared folder OR it is a single zap that is in a personal folder (not shared)
    if (
      onShowConfirmMoveToSharedFolder &&
      selectedFolderIsShared &&
      !assetsAreShared
    ) {
      // If the local storage key is not set, show the warning modal, else continue
      const shouldShowWarning =
        localStorage.getItem(HIDE_MOVE_ZAP_WARNING) !== "true";

      if (shouldShowWarning) {
        onShowConfirmMoveToSharedFolder?.({
          selectedFolderId,
          selectedFolderName,
        });
        return;
      }
    }

    if (!assetIds || assetIds?.length === 0) {
      return;
    }
    moveZapsToFolderWithToasts({
      folderId: currentFolderId,
      zapIds: assetIds,
      selectedFolderId,
      selectedFolderName,
      onUndo: handleUndoMoveZapsToFolder,
    });
    setIsOpen(false);
  };

  const handleUndoMoveZapsToFolder = ({ selectedFolderId }: OnMoveOptions) => {
    if (!assetIds || assetIds?.length === 0 || !selectedFolderId) {
      return;
    }
    undoMoveZapsToFolderWithToasts({
      selectedFolderId,
    });
    setIsOpen(false);
  };

  // Do not display the modal if isOpen==false
  if (!isOpen) {
    return;
  }

  return (
    <MoveModalInner
      assetType={AssetTypes.Zap}
      assetIds={assetIds}
      assetsAreShared={assetsAreShared}
      assetParentId={assetParentId}
      assetTitle={assetTitle}
      isAssetOwnedByUser={isAssetOwnedByUser}
      onMove={handleMoveZapsToFolder}
      setIsOpen={setIsOpen}
    />
  );
}
